// src/ui/SupportApp.jsx — allloapp.com/support: "Support AlloApp" (AlloApp_Support_Payments_Plan.md).
//
// The order of the page: who is signed in → what they already have (badge, monthly support, a payment still
// moving) → choose once / monthly, an amount, tick the consent → "Continue to secure payment" (Stripe's page) →
// back here: "Confirming…" until the SERVER says paid → thank you, and the badge choice. Manage support, history
// and receipts below. Never "donation", never "verified".
import React from 'react';
import './supportpay.css';
import {
  supportUser, supportConfig, supportStatus, startCheckout, cancelPending, changeSubscription, openPortal,
  setBadgeHidden, emailReceipt, watchAttempt, newAttemptId,
} from '../services/supportPay';
import { SupporterSeal, TIER_LABEL } from './SupporterBadge';
import { forgetSupporter } from '../services/supporters';

const L = {
  en: {
    title: 'Support AlloApp',
    lead: "AlloApp has no ads and never sells your data. It's built and run by a small team at Almatbaee Group LLC. If AlloApp helps you, you can support the team — one time or monthly.",
    once: 'One time', monthly: 'Monthly', custom: 'Other amount', perMonth: '/month',
    grantsOnce: (t, d) => `${t} badge for ${d} days`, grantsMonthly: (t) => `${t} badge while your monthly support is active`,
    consentTitle: 'Before you continue',
    pay: 'Continue to secure payment', paying: 'Opening secure payment…',
    stripeNote: 'Payments are processed by Stripe (card, Apple Pay, Google Pay). AlloApp never sees your card number.',
    signin: 'Sign in to continue', signinBody: 'Open AlloApp on your phone → Settings → Support AlloApp, or sign in to AlloApp Web on this computer.',
    signinBtn: 'Sign in to AlloApp Web',
    confirming: 'Confirming your payment…', stillConfirming: "Still confirming with your bank. Please don't pay again — this page updates by itself and your receipt is emailed when it completes.",
    thanks: 'Thank you!', thanksBody: (d, e) => `Your support keeps AlloApp ad-free. Your Supporter badge is active until ${d}.${e ? ` A receipt was sent to ${e}.` : ''}`,
    showBadge: 'Show Supporter badge on my profile', showBadgeSub: 'Others see it next to your name. Only the badge — never amounts or dates.',
    canceled: 'Payment canceled. You were not charged.', failed: "The payment didn't go through. You were not charged — try another card.",
    inProgress: 'Payment in progress', continuePay: 'Continue payment', cancelPay: 'Cancel this payment',
    yourBadge: 'Your badge', activeUntil: (d) => `Active until ${d}`, noBadge: 'No active badge',
    manage: 'Monthly support', nextRenewal: (d) => `Renews ${d}`, endsOn: (d) => `Ends ${d} — you canceled`, pastDue: "Your last payment didn't go through. Update your card to keep your support.",
    cancelSub: 'Cancel monthly support', cancelConfirm: 'Cancel your monthly support? Your badge stays until the end of the period you paid for.', resume: 'Keep my monthly support',
    change: 'Change amount', changeSave: 'Save new amount', changeNote: 'The new amount starts with your next renewal. Nothing is charged now.',
    updateCard: 'Update card', history: 'Payment history', noHistory: 'No payments yet.',
    viewReceipt: 'View receipt', emailReceipt: 'Email me this receipt', sent: 'Sent', refunded: 'Refunded', disputed: 'Disputed',
    kindOnce: 'One time', kindMonthly: 'Monthly',
    terms: 'Terms', privacy: 'Privacy', errors: {
      email_required: 'Add an email to your AlloApp account first (Settings → Account) so we can send your receipts.',
      under_18: 'You must be 18 or older to pay.', has_subscription: 'You already have monthly support — change its amount below.',
      payment_in_progress: 'You have a payment in progress. Wait for it to finish before paying again.', rate_limited: 'Too many attempts. Please wait a few minutes.',
      not_configured: 'Payments are not available yet.', paused: 'Payments are paused for a moment. Please try again later.',
      below_min: 'The amount is below the minimum.', above_max: 'The maximum is $500 per payment.', consent_required: 'Please tick the box to continue.',
      network: 'No connection. Please try again.', default: 'Something went wrong. Please try again.',
    },
  },
  ar: {
    title: 'ادعم AlloApp',
    lead: 'AlloApp بلا إعلانات ولا يبيع بياناتك أبدًا. يبنيه ويديره فريق صغير في Almatbaee Group LLC. إذا كان AlloApp يفيدك، يمكنك دعم الفريق — مرة واحدة أو شهريًا.',
    once: 'مرة واحدة', monthly: 'شهريًا', custom: 'مبلغ آخر', perMonth: '/شهر',
    grantsOnce: (t, d) => `شارة ${t} لمدة ${d} يومًا`, grantsMonthly: (t) => `شارة ${t} ما دام دعمك الشهري فعّالًا`,
    consentTitle: 'قبل المتابعة',
    pay: 'المتابعة إلى الدفع الآمن', paying: 'جارٍ فتح صفحة الدفع الآمن…',
    stripeNote: 'تتم المدفوعات عبر Stripe (البطاقة، Apple Pay، Google Pay). لا يرى AlloApp رقم بطاقتك أبدًا.',
    signin: 'سجّل الدخول للمتابعة', signinBody: 'افتح AlloApp على هاتفك ← الإعدادات ← ادعم AlloApp، أو سجّل الدخول إلى AlloApp Web على هذا الجهاز.',
    signinBtn: 'تسجيل الدخول إلى AlloApp Web',
    confirming: 'جارٍ تأكيد الدفع…', stillConfirming: 'ما زلنا نؤكد الدفع مع بنكك. لا تدفع مرة أخرى — ستتحدث هذه الصفحة تلقائيًا ويصلك الإيصال بالبريد عند الاكتمال.',
    thanks: 'شكرًا لك!', thanksBody: (d, e) => `دعمك يُبقي AlloApp بلا إعلانات. شارة الداعم فعّالة حتى ${d}.${e ? ` أُرسل الإيصال إلى ${e}.` : ''}`,
    showBadge: 'إظهار شارة الداعم في ملفي', showBadgeSub: 'يراها الآخرون بجانب اسمك. الشارة فقط — لا مبالغ ولا تواريخ.',
    canceled: 'أُلغي الدفع. لم يُخصم منك أي مبلغ.', failed: 'لم يتم الدفع. لم يُخصم منك أي مبلغ — جرّب بطاقة أخرى.',
    inProgress: 'دفعة قيد التنفيذ', continuePay: 'متابعة الدفع', cancelPay: 'إلغاء هذه الدفعة',
    yourBadge: 'شارتك', activeUntil: (d) => `فعّالة حتى ${d}`, noBadge: 'لا توجد شارة فعّالة',
    manage: 'الدعم الشهري', nextRenewal: (d) => `يتجدد في ${d}`, endsOn: (d) => `ينتهي في ${d} — لقد ألغيته`, pastDue: 'لم تنجح دفعتك الأخيرة. حدّث بطاقتك للحفاظ على دعمك.',
    cancelSub: 'إلغاء الدعم الشهري', cancelConfirm: 'هل تريد إلغاء دعمك الشهري؟ تبقى شارتك حتى نهاية الفترة المدفوعة.', resume: 'الإبقاء على دعمي الشهري',
    change: 'تغيير المبلغ', changeSave: 'حفظ المبلغ الجديد', changeNote: 'يبدأ المبلغ الجديد مع التجديد القادم. لا يُخصم شيء الآن.',
    updateCard: 'تحديث البطاقة', history: 'سجل المدفوعات', noHistory: 'لا توجد مدفوعات بعد.',
    viewReceipt: 'عرض الإيصال', emailReceipt: 'أرسل لي هذا الإيصال بالبريد', sent: 'أُرسل', refunded: 'مُستردّة', disputed: 'متنازع عليها',
    kindOnce: 'مرة واحدة', kindMonthly: 'شهري',
    terms: 'الشروط', privacy: 'الخصوصية', errors: {
      email_required: 'أضف بريدًا إلكترونيًا إلى حسابك في AlloApp أولًا (الإعدادات ← الحساب) لنرسل لك الإيصالات.',
      under_18: 'يجب أن يكون عمرك 18 عامًا أو أكثر للدفع.', has_subscription: 'لديك دعم شهري بالفعل — غيّر مبلغه بالأسفل.',
      payment_in_progress: 'لديك دفعة قيد التنفيذ. انتظر حتى تكتمل قبل الدفع مرة أخرى.', rate_limited: 'محاولات كثيرة. انتظر بضع دقائق من فضلك.',
      not_configured: 'الدفع غير متاح بعد.', paused: 'الدفع متوقف مؤقتًا. حاول لاحقًا.',
      below_min: 'المبلغ أقل من الحد الأدنى.', above_max: 'الحد الأقصى 500 دولار للدفعة.', consent_required: 'ضع علامة في المربع للمتابعة.',
      network: 'لا يوجد اتصال. حاول مرة أخرى.', default: 'حدث خطأ. حاول مرة أخرى.',
    },
    consent: {
      base: 'عمري 18 عامًا أو أكثر، وأفهم أن هذه دفعة طوعية إلى Almatbaee Group LLC لدعم AlloApp. ليست تبرعًا خيريًا ولا تُخصم من الضرائب. المدفوعات غير قابلة للاسترداد، إلا حيث يقتضي القانون أو قواعد شبكات البطاقات، أو إذا خُصم مني المبلغ عن طريق الخطأ.',
      monthly: 'سيتجدد دعمي تلقائيًا كل شهر بمبلغ {amount} حتى ألغيه من الإعدادات ← ادعم AlloApp. يمكنني الإلغاء في أي وقت، ويسري الإلغاء في نهاية الفترة الحالية.',
    },
  },
};

const usd = (c) => `$${(c / 100).toFixed(c % 100 ? 2 : 0)}`;
const errText = (t, e) => t.errors[e] || t.errors.default;
const langOf = () => {
  const q = new URLSearchParams(window.location.search).get('lang');
  const l = (q || navigator.language || 'en').toLowerCase();
  return l.startsWith('ar') ? 'ar' : 'en';
};
const ATTEMPT_KEY = 'alloapp_support_attempt';

function grantText(t, lang, cfg, kind, cents) {
  if (!cfg || !Number.isFinite(cents) || cents <= 0) return '';
  const names = TIER_LABEL[lang] || TIER_LABEL.en;
  if (kind === 'one_time') {
    let b = null; for (const x of cfg.oneTimeBands) if (cents >= x.from) b = x;
    return b ? t.grantsOnce(names[b.tier], b.days) : '';
  }
  let b = null; for (const x of cfg.monthlyBands) if (cents >= x.from) b = x;
  return b ? t.grantsMonthly(names[b.tier]) : '';
}
const tierFor = (cfg, kind, cents) => {
  if (!cfg) return null;
  let b = null; for (const x of (kind === 'one_time' ? cfg.oneTimeBands : cfg.monthlyBands)) if (cents >= x.from) b = x;
  return b ? b.tier : null;
};

export default function SupportApp() {
  const lang = React.useMemo(langOf, []);
  const t = L[lang];
  const fmtDate = React.useCallback((ms) => { try { return new Date(ms).toLocaleDateString(lang === 'ar' ? 'ar' : undefined, { year: 'numeric', month: 'long', day: 'numeric' }); } catch { return new Date(ms).toDateString(); } }, [lang]);
  const [who, setWho] = React.useState(undefined);   // undefined: checking; null: signed out
  const [cfg, setCfg] = React.useState(null);
  const [st, setSt] = React.useState(null);
  const [kind, setKind] = React.useState('one_time');
  const [cents, setCents] = React.useState(500);
  const [custom, setCustom] = React.useState('');
  const [agree, setAgree] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [note, setNote] = React.useState('');
  const [watch, setWatch] = React.useState(null);   // { attemptId, status, slow }
  const [changeTo, setChangeTo] = React.useState(null);
  const [changeAgree, setChangeAgree] = React.useState(false);
  const [sentIds, setSentIds] = React.useState({});

  React.useEffect(() => {
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.title = t.title;
    supportConfig().then(setCfg).catch(() => setCfg({ configured: false }));
    supportUser().then((w) => setWho(w || null)).catch(() => setWho(null));
  }, [lang, t.title]);

  const refresh = React.useCallback(async () => { if (who && who.user) { const s = await supportStatus(who.user); if (s && s.ok) setSt(s); } }, [who]);
  React.useEffect(() => { void refresh(); }, [refresh]);

  /* back from Stripe's page: ?attempt=… — watch it until the server has the answer */
  React.useEffect(() => {
    if (!who || !who.user) return undefined;
    const p = new URLSearchParams(window.location.search);
    const attemptId = p.get('attempt');
    if (!attemptId) return undefined;
    if (p.get('back')) { setNote(t.canceled); sessionStorage.removeItem(ATTEMPT_KEY); window.history.replaceState(null, '', '/support'); return undefined; }
    setWatch({ attemptId, status: 'created', slow: false });
    const slow = setTimeout(() => setWatch((w) => (w ? { ...w, slow: true } : w)), 20000);
    const off = watchAttempt(who.app, who.user, attemptId, (status) => {
      setWatch((w) => (w ? { ...w, status } : w));
      if (['succeeded', 'canceled', 'failed', 'refunded'].includes(status)) {
        sessionStorage.removeItem(ATTEMPT_KEY);
        forgetSupporter(who.user.uid);
        void refresh();
      }
    });
    return () => { clearTimeout(slow); off(); };
  }, [who, refresh, t.canceled]);

  const amount = custom ? Math.round(Number(custom.replace(/[^0-9.]/g, '')) * 100) : cents;
  const min = cfg ? (kind === 'monthly' ? cfg.monthlyMin : cfg.min) : 300;
  const valid = cfg && cfg.configured && Number.isFinite(amount) && amount >= min && amount <= cfg.max;
  const consentWords = React.useMemo(() => {
    if (!cfg || !cfg.consentText) return '';
    const src = lang === 'ar' ? L.ar.consent : cfg.consentText;
    return kind === 'monthly' ? `${src.base} ${src.monthly.replace('{amount}', usd(amount || 0))}` : src.base;
  }, [cfg, lang, kind, amount]);

  const pay = async () => {
    if (!valid || !agree || busy || !who) return;
    setBusy(true); setErr(''); setNote('');
    /* one attempt per choice: a double click, a retry or a reload asks for the SAME attempt (never a second charge) */
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(ATTEMPT_KEY) || 'null'); } catch {}
    const attemptId = saved && saved.kind === kind && saved.amount === amount && Date.now() - saved.at < 25 * 60 * 1000 ? saved.id : newAttemptId();
    sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify({ id: attemptId, kind, amount, at: saved && saved.id === attemptId ? saved.at : Date.now() }));
    let r = await startCheckout(who.user, { attemptId, kind, amountCents: amount, consentVersion: cfg.consentVersion, consented: true, lang }).catch(() => ({ ok: false, error: 'network' }));
    if (r && r.error === 'attempt_used') {
      /* that attempt is spent (finished, canceled, or made by an older page): a fresh one, once, by itself */
      const fresh = newAttemptId();
      sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify({ id: fresh, kind, amount, at: Date.now() }));
      r = await startCheckout(who.user, { attemptId: fresh, kind, amountCents: amount, consentVersion: cfg.consentVersion, consented: true, lang }).catch(() => ({ ok: false, error: 'network' }));
    }
    if (r && r.ok && r.url) { window.location.assign(r.url); return; }
    if (r && r.error === 'attempt_used') sessionStorage.removeItem(ATTEMPT_KEY);
    setErr(`${errText(t, r && r.error)}${r && r.detail ? ` (${r.detail})` : ''}`); setBusy(false);
    void refresh();
  };

  const toggleBadge = async (show) => {
    if (!who) return;
    const r = await setBadgeHidden(who.user, !show);
    if (r && r.ok) { forgetSupporter(who.user.uid); void refresh(); }
  };
  const subAction = async (body) => {
    if (!who) return;
    setErr('');
    const r = await changeSubscription(who.user, body);
    if (r && r.ok) { setSt(r); setChangeTo(null); setChangeAgree(false); } else setErr(errText(t, r && r.error));
  };
  const portal = async () => { const r = await openPortal(who.user); if (r && r.url) window.location.assign(r.url); else setErr(errText(t, r && r.error)); };
  const resend = async (id) => { const r = await emailReceipt(who.user, id); if (r && r.ok) setSentIds((s) => ({ ...s, [id]: true })); else setErr(errText(t, r && r.error)); };
  const dropPending = async (id) => { await cancelPending(who.user, id); sessionStorage.removeItem(ATTEMPT_KEY); void refresh(); };

  const names = TIER_LABEL[lang] || TIER_LABEL.en;
  const sub = st && st.subscription;
  const sup = st && st.supporter;
  const pending = st && st.pending;

  return (
    <div className="spay-page">
      <div className="spay-card">
        <div className="spay-head">
          <div className="spay-brand">AlloApp</div>
          <h1>{t.title}</h1>
          <p className="spay-lead">{t.lead}</p>
        </div>

        {who === undefined ? <div className="spay-spin" aria-busy="true" /> : null}

        {who === null ? (
          <div className="spay-box">
            <h2>{t.signin}</h2>
            <p>{t.signinBody}</p>
            <a className="spay-btn" href="/app">{t.signinBtn}</a>
          </div>
        ) : null}

        {who && watch ? (
          <div className="spay-box spay-center" aria-live="polite">
            {watch.status === 'succeeded' ? (
              <>
                <div className="spay-big-badge">{sup && sup.tier ? <SupporterSeal tier={sup.tier} size={64} title={names[sup.tier]} /> : null}</div>
                <h2>{t.thanks}</h2>
                <p>{t.thanksBody(sup && sup.expiresAtMs ? fmtDate(sup.expiresAtMs) : '…', st && st.email)}</p>
                <label className="spay-switch">
                  <input type="checkbox" checked={!(sup && sup.hidden)} onChange={(e) => toggleBadge(e.target.checked)} />
                  <span><b>{t.showBadge}</b><small>{t.showBadgeSub}</small></span>
                </label>
                <button type="button" className="spay-link" onClick={() => { setWatch(null); window.history.replaceState(null, '', '/support'); }}>OK</button>
              </>
            ) : watch.status === 'canceled' || watch.status === 'failed' ? (
              <>
                <p className="spay-err">{watch.status === 'failed' ? t.failed : t.canceled}</p>
                <button type="button" className="spay-btn" onClick={() => { setWatch(null); window.history.replaceState(null, '', '/support'); }}>OK</button>
              </>
            ) : (
              <>
                <div className="spay-spin" aria-busy="true" />
                <h2>{t.confirming}</h2>
                {watch.slow ? <p>{t.stillConfirming}</p> : null}
              </>
            )}
          </div>
        ) : null}

        {who && !watch ? (
          <>
            {sup ? (
              <div className="spay-row spay-status">
                {sup.tier ? <SupporterSeal tier={sup.tier} size={34} title={names[sup.tier]} /> : null}
                <div>
                  <b>{sup.tier ? names[sup.tier] : t.noBadge}</b>
                  {sup.tier && sup.expiresAtMs ? <small>{t.activeUntil(fmtDate(sup.expiresAtMs))}</small> : null}
                </div>
                {sup.tier ? (
                  <label className="spay-mini-switch" title={t.showBadge}>
                    <input type="checkbox" checked={!sup.hidden} onChange={(e) => toggleBadge(e.target.checked)} />
                    <span>{t.showBadge}</span>
                  </label>
                ) : null}
              </div>
            ) : null}

            {pending ? (
              <div className="spay-box spay-warn">
                <b>{t.inProgress}</b> — {usd(pending.amountCents)} {pending.kind === 'one_time' ? t.kindOnce : t.kindMonthly}
                <div className="spay-actions">
                  {pending.url ? <a className="spay-btn spay-small" href={pending.url}>{t.continuePay}</a> : null}
                  {pending.status === 'created' ? <button type="button" className="spay-link" onClick={() => dropPending(pending.attemptId)}>{t.cancelPay}</button> : null}
                </div>
              </div>
            ) : null}

            {note ? <p className="spay-note">{note}</p> : null}
            {err ? <p className="spay-err" role="alert">{err}</p> : null}

            {sub ? (
              <div className="spay-box">
                <h2>{t.manage}</h2>
                <p><b>{usd(sub.amountCents)}{t.perMonth}</b> · {sub.cancelAtPeriodEnd ? t.endsOn(fmtDate(sub.currentPeriodEndMs)) : t.nextRenewal(fmtDate(sub.currentPeriodEndMs))}</p>
                {sub.status === 'past_due' ? <p className="spay-err">{t.pastDue}</p> : null}
                {changeTo !== null ? (
                  <div className="spay-change">
                    <div className="spay-amounts">
                      {(cfg ? cfg.presets.monthly : []).map((c) => <button type="button" key={c} className={c === changeTo ? 'on' : ''} onClick={() => setChangeTo(c)}>{usd(c)}{t.perMonth}</button>)}
                    </div>
                    <label className="spay-consent"><input type="checkbox" checked={changeAgree} onChange={(e) => setChangeAgree(e.target.checked)} /><span>{lang === 'ar' ? `${L.ar.consent.base} ${L.ar.consent.monthly.replace('{amount}', usd(changeTo))}` : `${cfg.consentText.base} ${cfg.consentText.monthly.replace('{amount}', usd(changeTo))}`}</span></label>
                    <p className="spay-small-note">{t.changeNote}</p>
                    <button type="button" className="spay-btn" disabled={!changeAgree || changeTo === sub.amountCents} onClick={() => subAction({ action: 'change', amountCents: changeTo, consentVersion: cfg.consentVersion, consented: true })}>{t.changeSave}</button>
                  </div>
                ) : (
                  <div className="spay-actions">
                    {sub.cancelAtPeriodEnd
                      ? <button type="button" className="spay-btn spay-small" onClick={() => subAction({ action: 'resume' })}>{t.resume}</button>
                      : <button type="button" className="spay-link spay-danger" onClick={() => { if (window.confirm(t.cancelConfirm)) void subAction({ action: 'cancel' }); }}>{t.cancelSub}</button>}
                    <button type="button" className="spay-link" onClick={() => { setChangeTo(sub.amountCents); setChangeAgree(false); }}>{t.change}</button>
                    <button type="button" className="spay-link" onClick={portal}>{t.updateCard}</button>
                  </div>
                )}
              </div>
            ) : null}

            {!pending ? (
              <div className="spay-box">
                <div className="spay-tabs" role="tablist">
                  <button type="button" role="tab" aria-selected={kind === 'one_time'} className={kind === 'one_time' ? 'on' : ''} onClick={() => { setKind('one_time'); setCents(500); setCustom(''); }}>{t.once}</button>
                  {!sub ? <button type="button" role="tab" aria-selected={kind === 'monthly'} className={kind === 'monthly' ? 'on' : ''} onClick={() => { setKind('monthly'); setCents(1000); setCustom(''); }}>{t.monthly}</button> : null}
                </div>
                <div className="spay-amounts">
                  {(cfg && cfg.presets ? cfg.presets[kind] : []).map((c) => (
                    <button type="button" key={c} className={!custom && c === cents ? 'on' : ''} onClick={() => { setCents(c); setCustom(''); }}>
                      {usd(c)}{kind === 'monthly' ? <small>{t.perMonth}</small> : null}
                    </button>
                  ))}
                </div>
                <label className="spay-custom">
                  <span>{t.custom}</span>
                  <span className="spay-dollar">$<input inputMode="decimal" value={custom} placeholder={(min / 100).toFixed(0)} onChange={(e) => setCustom(e.target.value.slice(0, 7))} /></span>
                </label>
                {valid ? (
                  <p className="spay-grant">
                    {tierFor(cfg, kind, amount) ? <SupporterSeal tier={tierFor(cfg, kind, amount)} size={18} title="" /> : null}
                    <span>{grantText(t, lang, cfg, kind, amount)}</span>
                  </p>
                ) : null}
                <div className="spay-consent-box">
                  <b>{t.consentTitle}</b>
                  <label className="spay-consent"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} /><span>{consentWords}</span></label>
                </div>
                <button type="button" className="spay-btn spay-pay" disabled={!valid || !agree || busy} onClick={pay}>
                  {busy ? t.paying : `${t.pay} · ${valid ? usd(amount) : ''}${kind === 'monthly' && valid ? t.perMonth : ''}`}
                </button>
                <p className="spay-small-note">{t.stripeNote}</p>
              </div>
            ) : null}

            {st ? (
              <div className="spay-box">
                <h2>{t.history}</h2>
                {st.history && st.history.length ? (
                  <ul className="spay-history">
                    {st.history.map((h) => (
                      <li key={h.id}>
                        <div>
                          <b>{usd(h.amountCents)}</b> · {h.kind === 'one_time' ? t.kindOnce : t.kindMonthly}
                          {h.status === 'refunded' ? <em> · {t.refunded}</em> : h.status === 'disputed' ? <em> · {t.disputed}</em> : null}
                          <small>{h.paidAtMs ? fmtDate(h.paidAtMs) : ''}{h.cardLast4 ? ` · ${String(h.cardBrand || '').toUpperCase()} •••• ${h.cardLast4}` : ''}</small>
                        </div>
                        <div className="spay-actions">
                          {h.receiptUrl ? <a className="spay-link" href={h.receiptUrl} target="_blank" rel="noreferrer">{t.viewReceipt}</a> : null}
                          <button type="button" className="spay-link" disabled={!!sentIds[h.id]} onClick={() => resend(h.id)}>{sentIds[h.id] ? t.sent : t.emailReceipt}</button>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : <p className="spay-muted">{t.noHistory}</p>}
              </div>
            ) : null}
          </>
        ) : null}

        <footer className="spay-foot">
          <span>Almatbaee Group LLC</span> · <a href="/terms">{t.terms}</a> · <a href="/privacy">{t.privacy}</a>
        </footer>
      </div>
    </div>
  );
}
